import React, { useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import ProductGrid from './ProductGrid';

export default function SearchPage({ products, addToCart, onProductClick, wishlistIds, onToggleFav }) {
    const [searchParams] = useSearchParams();
    const query = (searchParams.get('q') || '').trim();
    const q = query.toLowerCase();

    // SEO: title + description + noindex для страницы поиска
    // noindex обязателен: страницы /search?q=* — мусор для индекса,
    // они конкурируют с коммерческими посадочными (/category/*, /product/*)
    useEffect(() => {
        // 1. Title
        document.title = query
            ? `Поиск: ${query} — Pure Synergy`
            : 'Поиск по каталогу — Pure Synergy';

        // 2. Meta description
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
            metaDesc.setAttribute(
                'content',
                query
                    ? `Результаты поиска «${query}» в каталоге Pure Synergy: профессиональная космецевтика с доставкой по РФ.`
                    : 'Поиск по каталогу Pure Synergy: сыворотки, кремы, SPF, наборы.'
            );
        }

        // 3. Meta robots: noindex, follow
        //    - noindex: сама страница не попадает в индекс
        //    - follow: ссылки на товары внутри — обходятся
        let robots = document.querySelector('meta[name="robots"]');
        const created = !robots;
        const prevContent = robots ? robots.getAttribute('content') : null;

        if (!robots) {
            robots = document.createElement('meta');
            robots.setAttribute('name', 'robots');
            document.head.appendChild(robots);
        }
        robots.setAttribute('content', 'noindex, follow');

        // Cleanup: возвращаем всё как было при уходе со страницы
        return () => {
            if (created) {
                robots.remove();
            } else if (prevContent !== null) {
                robots.setAttribute('content', prevContent);
            }
        };
    }, [query]);

    // Поиск по name + category + keywords + brand + description
    const results = useMemo(() => {
        if (!q) return [];
        return products.filter((p) => {
            const text = [
                p.name || '',
                p.category || '',
                p.keywords || '',
                p.brand || '',
                p.description || '',
            ]
                .join(' ')
                .toLowerCase();
            return text.includes(q);
        });
    }, [products, q]);

    return (
        <div className="max-w-6xl mx-auto py-8">
            {/* Хлебные крошки */}
            <nav className="text-xs text-gray-500 mb-4">
                <Link to="/" className="hover:text-[#6B7A6A] hover:underline">
                    Каталог
                </Link>
                <span className="mx-1">/</span>
                <span className="text-[#1C1917]">Поиск</span>
            </nav>

            <h1 className="serif text-3xl md:text-4xl text-[#1C1917] mb-3">
                {query ? <>Результаты поиска: «{query}»</> : 'Поиск по каталогу'}
            </h1>

            {!query ? (
                <div className="text-gray-600">
                    <p className="mb-6">
                        Введите запрос в строке поиска в верхней части страницы.
                    </p>
                    <p className="text-sm text-gray-500 mb-3">Или выберите категорию:</p>
                    <div className="flex flex-wrap gap-3">
                        <Link
                            to="/category/syvorotki"
                            className="px-4 py-2 rounded-full bg-[#F5F0EB] text-sm hover:bg-[#E8E3DD] transition"
                        >
                            Сыворотки
                        </Link>
                        <Link
                            to="/category/uvlazhnyayushchie"
                            className="px-4 py-2 rounded-full bg-[#F5F0EB] text-sm hover:bg-[#E8E3DD] transition"
                        >
                            Увлажняющие
                        </Link>
                        <Link
                            to="/category/ochishchayushchie"
                            className="px-4 py-2 rounded-full bg-[#F5F0EB] text-sm hover:bg-[#E8E3DD] transition"
                        >
                            Очищающие
                        </Link>
                        <Link
                            to="/category/spf"
                            className="px-4 py-2 rounded-full bg-[#F5F0EB] text-sm hover:bg-[#E8E3DD] transition"
                        >
                            SPF
                        </Link>
                        <Link
                            to="/category/nabory"
                            className="px-4 py-2 rounded-full bg-[#F5F0EB] text-sm hover:bg-[#E8E3DD] transition"
                        >
                            Наборы
                        </Link>
                    </div>
                </div>
            ) : (
                <>
                    <p className="text-gray-600 mb-6">
                        {results.length > 0 ? (
                            <>
                                Найдено: <strong>{results.length}</strong>{' '}
                                {results.length === 1
                                    ? 'товар'
                                    : results.length < 5
                                        ? 'товара'
                                        : 'товаров'}
                            </>
                        ) : (
                            <>Ничего не нашли по запросу «{query}»</>
                        )}
                    </p>

                    {results.length > 0 ? (
                        <ProductGrid
                            products={results}
                            addToCart={addToCart}
                            onProductClick={onProductClick}
                            wishlistIds={wishlistIds}     
                            onToggleFav={onToggleFav} 
                        />
                    ) : (
                        <div className="text-center py-12">
                            <p className="text-sm text-gray-400 mb-6">
                                Попробуйте изменить запрос или посмотрите категории:
                            </p>
                            <div className="flex flex-wrap gap-3 justify-center">
                                <Link
                                    to="/category/syvorotki"
                                    className="px-4 py-2 rounded-full bg-[#F5F0EB] text-sm hover:bg-[#E8E3DD] transition"
                                >
                                    Сыворотки
                                </Link>
                                <Link
                                    to="/category/uvlazhnyayushchie"
                                    className="px-4 py-2 rounded-full bg-[#F5F0EB] text-sm hover:bg-[#E8E3DD] transition"
                                >
                                    Увлажняющие
                                </Link>
                                <Link
                                    to="/category/ochishchayushchie"
                                    className="px-4 py-2 rounded-full bg-[#F5F0EB] text-sm hover:bg-[#E8E3DD] transition"
                                >
                                    Очищающие
                                </Link>
                                <Link
                                    to="/category/spf"
                                    className="px-4 py-2 rounded-full bg-[#F5F0EB] text-sm hover:bg-[#E8E3DD] transition"
                                >
                                    SPF
                                </Link>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}